import { Formik } from "formik";
import { useEffect, useState } from "react";
import Spin from "../../../components/Spin";
import { Label } from "../../../components/ui/label";
import CustomLabelText from "../../../components/CustomLabelText";
import { Input } from "../../../components/ui/input";
import { API, fileUpload, get } from "../../../config";
import { Button } from "../../../components/ui/button";
import { AddAdminModuleSchema } from "../../../lib/Validation";
import {
  useAllModules,
  useAllOrganizationTypes,
} from "../../Auth/SignUpHelper";
import SearchSelect from "../../../components/SearchSelect";
import { Loader2 } from "lucide-react";

const AddEditModuleNaming = ({
  id,
  toggleShowAddEditModule,
  edit,
  fetchModuleData,
  orgId,
}) => {
  const [moduleData, setModuleData] = useState([]);
  const [loading, setLoading] = useState(false);

  const { allOrganizationTypeLoading, allOrganizationType = [] } =
    useAllOrganizationTypes({ amlCTFType: false });
  const organizationType = allOrganizationType?.map((item) => {
    const { id: value, name, short_name, ...rest } = item;
    return { label: name, value, ...rest };
  });

  const { allModuleLoading, allModule = [] } = useAllModules();
  const module = allModule?.map((item) => {
    const { id: value, name, short_name, ...rest } = item;
    return { label: name, value, ...rest };
  });

  async function fetchData() {
    try {
      setLoading(true);
      const reponse = await get(`${API.GET_ADMIN_MODULE_LIST}/` + id);
      setModuleData(reponse?.data?.data);
      setLoading(false);
    } catch (e) {
      setModuleData(null);
    }
  }

  useEffect(() => {
    if (edit) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleFormSubmit = async (value) => {
    const formData = new FormData();

    formData.append("name", value?.name ? value?.name : "");
    formData.append("organization_type_id", value?.organization_type_id ? value?.organization_type_id : "");
    formData.append("module_id", value?.module_id ? value?.module_id : "");
    

    if (edit) {
      formData.append("_method", "put");
    }
    try {
      setLoading(true);
      const { data } = await fileUpload(
        `${API.GET_ADMIN_MODULE_LIST}${edit ? `/${id}` : ""}`,
        formData
      );
      setLoading(false);
      if (data) {
        toggleShowAddEditModule();
        fetchModuleData();
      }
      return data?.data;
    } catch (error) {
      setLoading(false);
    }
  };

  return (
    <Formik
      initialValues={{
        name: moduleData?.name,
        organization_type_id: moduleData?.organization_type_id ? moduleData?.organization_type_id : "",
        module_id: moduleData?.module_id ? moduleData?.module_id : "",
      }}
      enableReinitialize={true}
      onSubmit={handleFormSubmit}
      validateOnBlur={true}
      validateOnChange={true}
      validationSchema={AddAdminModuleSchema}
    >
      {({
        values,
        errors,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldValue,
        isValid,
      }) => (
        <>
          {loading && <Spin fullscreen={true}/>}
          <form
            onSubmit={handleSubmit}
            noValidate
            className="entry-section md:m-0 m-5"
          >
            <div className="form-box overflow-y-auto overflow-x-hidden max-h-[80vh] p-4 border rounded-lg grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="type">
                  <CustomLabelText
                    required={true}
                    labelText="Industry Type"
                    errorField={errors?.organization_type_id}
                    bottomAlign={true}
                  />
                </Label>
                <div className="relative">
                  <SearchSelect
                    frameworks={organizationType}
                    optionType={"Industry Type"}
                    handleSelect={(e) => {
                      setFieldValue("organization_type_id", e);
                    }}
                    handleBlur={handleBlur}
                    className={
                      errors?.organization_type_id &&
                      `focus-visible:ring-red-500 border-red-500`
                    }
                    initialValue={values?.organization_type_id}
                    disabled={false}
                  />
                  {allOrganizationTypeLoading && (
                    <span className="absolute top-2 right-2 animate-spin">
                      <Loader2 width={20} height={20} />
                    </span>
                  )}
                </div>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="type">
                  <CustomLabelText
                    required={true}
                    labelText="Module"
                    errorField={errors?.module_id}
                  />
                </Label>
                <div className="relative">
                  <SearchSelect
                    frameworks={module}
                    optionType={"Module"}
                    handleSelect={(e) => {
                      setFieldValue("module_id", e);
                    }}
                    handleBlur={handleBlur}
                    className={
                      errors?.module_id &&
                      `focus-visible:ring-red-500 border-red-500`
                    }
                    initialValue={values?.module_id}
                    disabled={false}
                  />
                  {allModuleLoading && (
                    <span className="absolute top-2 right-2 animate-spin">
                      <Loader2 width={20} height={20} />
                    </span>
                  )}
                </div>
              </div>
              <div className="grid gap-4">
                <div className="grid gap-2 col-span-2">
                  <Label htmlFor="name">
                    <CustomLabelText
                      required={true}
                      labelText="Name"
                      errorField={errors?.name}
                    />
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    name="name"
                    value={values?.name ? values?.name : ""}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    autoComplete="off"
                    onKeyPress={(e) => {
                      e.which === 13 && e.preventDefault();
                    }}
                    className={
                      errors?.name &&
                      `focus-visible:ring-red-500 border-red-500`
                    }
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer pt-4 border-t mt-4 flex items-center justify-end">
              <Button type="submit" disabled={!isValid}>
                Save
              </Button>
              <Button
                className="reset ml-3"
                data-dismiss="modal"
                aria-label="Close"
                onClick={(e) => {
                  e.preventDefault();
                  toggleShowAddEditModule();
                }}
                variant="secondary"
              >
                Cancel
              </Button>
            </div>
          </form>
        </>
      )}
    </Formik>
  );
};

export default AddEditModuleNaming;
